import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabase-config";
import { withLocalWorkImage } from "../../assets/workImages";

const DATA_BASE = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/media/data`;

function mapRows(rows, collectionData) {
  return (rows || [])
    .map((row) =>
      withLocalWorkImage(
        {
          id: row.id,
          title: row.title,
          description: row.description,
          link: row.link,
          producer: row.producer,
          date: row.date,
          ImageUrl: row.image_url ?? row.ImageUrl,
          ImageIsScoresHome: row.image_is_scores_home
            ? "true"
            : row.ImageIsScoresHome,
          sort_order:
            row.sort_order != null
              ? Number(row.sort_order)
              : row.sortOrder != null
                ? Number(row.sortOrder)
                : 0,
        },
        collectionData
      )
    )
    .sort((a, b) => {
      const ao = Number(a.sort_order) || 0;
      const bo = Number(b.sort_order) || 0;
      if (ao !== bo) return ao - bo;
      return String(a.id).localeCompare(String(b.id));
    });
}

export const useGetFirebaseStore = (collectionData) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(Boolean(collectionData));
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!collectionData) return;
    setLoading(true);
    setError(null);
    try {
      const ordered = await supabase
        .from(collectionData)
        .select("*")
        .order("sort_order", { ascending: true })
        .order("id", { ascending: true });

      let rows = ordered.data;
      let queryError = ordered.error;

      if (queryError) {
        const fallback = await supabase.from(collectionData).select("*");
        rows = fallback.data;
        queryError = fallback.error;
      }

      const res = await fetch(
        `${DATA_BASE}/${collectionData}.json?t=${Date.now()}`
      );
      const json = res.ok ? await res.json() : [];
      const jsonRows = Array.isArray(json) ? json : [];
      const tableRows = !queryError && Array.isArray(rows) ? rows : [];

      // A partial table row (e.g. a single upsert) must not hide the JSON archive.
      const source =
        jsonRows.length > tableRows.length ? jsonRows : tableRows.length ? tableRows : jsonRows;
      if (source.length) return mapRows(source, collectionData);

      if (queryError) throw queryError;
      return [];
    } catch (err) {
      throw err;
    }
  }, [collectionData]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    refetch()
      .then((rows) => {
        if (cancelled) return;
        setData(rows || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        setData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refetch]);

  const deleteData = async (id) => {
    if (!id) {
      setError(new Error("Missing id"));
      return;
    }
    const { error: delError } = await supabase
      .from(collectionData)
      .delete()
      .eq("id", id);
    if (delError) {
      setError(delError);
      return;
    }
    const rows = await refetch();
    setData(rows || []);
  };

  return { data, loading, error, deleteData, refetch };
};
